import type { School } from '@kimuno/api-sdk/lib/types/responses/Text';
import { FormLabel } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { updateMe as updateMeAction } from '../../../actions/user.actions';
import { SchoolNameSelected } from '../../../ampli';
import OnboardingStepSchuleImage from '../../../assets/onboarding/onboarding-step-schule.svg';
import { palette } from '../../../assets/styles/themes/tokens';
import { selectUser } from '../../../reducers/user.selectors';
import type { OnboardingSteps } from '../../../routes/onboarding/OnboardingPage';
import services from '../../../services';
import useAction from '../../../utils/useAction';
import OnboardingStepContainer from '../OnboardingStepContainer';
import OnboardingStepImage from '../OnboardingStepImage';
import SchoolAutocomplete from './SchoolAutocomplete';

interface OwnProps {
	setOnboardingStep: (step: keyof OnboardingSteps) => void;
	stepNum?: number;
	totalSteps?: number;
	onBackOverride?: (() => void) | null;
	onNextOverride?: () => void;
	hideImage?: boolean;
	compact?: boolean;
}

const DEBOUNCE_MS = 300;
const MIN_INPUT_LENGTH = 3;

const OnboardingStepSchule: React.FC<OwnProps> = ({
	setOnboardingStep,
	stepNum,
	totalSteps,
	onBackOverride,
	onNextOverride,
	hideImage,
	compact,
}) => {
	const { t } = useTranslation();
	const user = useSelector(selectUser);

	const [inputValue, setInputValue] = useState('');
	const [selectedSchool, setSelectedSchool] = useState<School>({
		id: '',
		name: '',
		formattedAddress: '',
		state: '',
	});
	const [options, setOptions] = useState<School[]>([]);
	const [loading, setLoading] = useState(false);
	const updateMe = useAction(updateMeAction);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const contentHeights = {
		default: 'calc(100vh - 527px)',
		smallScreen: 'calc(100vh - 353px)',
	};

	const handleNext = async () => {
		await updateMe({
			school: {
				placeId: selectedSchool?.id || '',
				name: selectedSchool?.name || '',
				formattedAddress: selectedSchool?.formattedAddress || '',
				state: selectedSchool?.state || '',
			},
		});

		if (user) {
			services.track.ampliEventTrack({
				event: new SchoolNameSelected({
					'school name': selectedSchool.name,
				}),
				userProperties: {
					id: user.id,
					schoolName: selectedSchool.name,
				},
			});
		}

		if (onNextOverride) {
			onNextOverride();
		} else {
			setOnboardingStep('bundesland');
		}
	};

	useEffect(() => {
		if (user?.school) {
			setSelectedSchool({
				id: user.school.placeId,
				name: user.school.name,
				formattedAddress: user.school.formattedAddress,
				state: user.school.state,
			});
			setInputValue(user.school.name);
		}
	}, [user?.school]);

	// Debounce logic
	useEffect(() => {
		if (debounceRef.current) clearTimeout(debounceRef.current);

		debounceRef.current = setTimeout(async () => {
			if (inputValue.length > MIN_INPUT_LENGTH) {
				setLoading(true);
				try {
					const schools = await services.text.fetchSchools({
						textQuery: inputValue,
					});
					setOptions(schools || []);
				} catch {
					setOptions([]);
				}
				setLoading(false);
			} else {
				setOptions([]);
			}
		}, DEBOUNCE_MS);

		return () => {
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}
		};
	}, [inputValue]);

	return (
		<>
			<OnboardingStepContainer
				title={t('Which school do you teach at?')}
				subtitle={t('This makes it easy to find and connect with other teachers from your school.')}
				stepNum={stepNum ?? 2}
				totalSteps={totalSteps}
				onBack={
					onBackOverride === undefined ? () => setOnboardingStep('roles') : (onBackOverride ?? undefined)
				}
				onNext={handleNext}
				contentHeights={contentHeights}
				compact={compact}
			>
				<FormLabel sx={{ color: palette.text.primary }}>{t('School name')}</FormLabel>
				<SchoolAutocomplete
					value={selectedSchool}
					inputValue={inputValue}
					options={options}
					loading={loading}
					onInputChange={setInputValue}
					onChange={(value) => {
						setSelectedSchool(value);
						if (!value) setInputValue('');
					}}
					placeholder={t('Enter school')}
					noOptionsText={t('No options')}
					loadingText={t('Loading') + '...'}
					setValue={setSelectedSchool}
					setInputValue={setInputValue}
				/>
			</OnboardingStepContainer>
			{!hideImage && <OnboardingStepImage stepImage={<OnboardingStepSchuleImage />} />}
		</>
	);
};

export default OnboardingStepSchule;
